import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Row, Spinner } from 'react-bootstrap';
import { AlertTriangle, CheckCircle, Clock, FileText, Save } from 'lucide-react';
import { AdminApiDomain, getAdminApiUrl } from '@/config/ApiRouting';
import { SiteConfigClient } from '@/infrastructure/SiteConfigClient';
import {
  DEFAULT_GIG_CART_VERIFICATION_PROMPT_CONFIG,
  GIG_CART_VERIFICATION_PROMPT_CONFIG_KEY,
} from '@/models/GigCartVerificationPromptConfig';
import { validateGigCartVerificationPromptConfig } from '@/pages/oms/gig-cart-verification-prompt/GigCartVerificationPromptConfigValidator';

export default function GigCartVerificationPromptPage() {
  const [configText, setConfigText] = useState('');
  const [savedText, setSavedText] = useState('');
  const [version, setVersion] = useState(0);
  const [notYetCreated, setNotYetCreated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const clientRef = useRef<SiteConfigClient | null>(null);

  const getClient = useCallback(() => {
    if (!clientRef.current) {
      clientRef.current = new SiteConfigClient(getAdminApiUrl(AdminApiDomain.SITE_CONFIG));
    }
    return clientRef.current;
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const entry = await getClient().getSiteConfig(GIG_CART_VERIFICATION_PROMPT_CONFIG_KEY);
        const text = JSON.stringify(entry.config, null, 2);
        setConfigText(text);
        setSavedText(text);
        setVersion(entry.version);
        setNotYetCreated(false);
      } catch (loadError) {
        const status = (loadError as { response?: { status?: number } } | null)?.response?.status;
        if (status === 404) {
          setConfigText(JSON.stringify(DEFAULT_GIG_CART_VERIFICATION_PROMPT_CONFIG, null, 2));
          setSavedText('');
          setNotYetCreated(true);
        } else {
          setError(loadError instanceof Error ? loadError.message : 'Failed to load config');
        }
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [getClient]);

  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    let parsed: unknown;
    try {
      parsed = JSON.parse(configText);
    } catch {
      setError('Invalid JSON — fix the syntax before saving');
      return;
    }

    const validationError = validateGigCartVerificationPromptConfig(parsed);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!window.confirm('Save these examples? They will affect new Gig cart verifications immediately.')) {
      return;
    }

    setSaving(true);
    try {
      const entry = await getClient().setSiteConfig(
        GIG_CART_VERIFICATION_PROMPT_CONFIG_KEY,
        parsed as Record<string, unknown>,
      );
      setVersion(entry.version);
      setSavedText(configText);
      setNotYetCreated(false);
      setSuccess('Gig cart verification prompt examples saved');
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = configText !== savedText;

  if (loading) {
    return (
      <Container fluid className="py-4 d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container fluid className="py-3 px-3" style={{ height: 'calc(100vh - 56px)' }}>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
          <FileText size={24} />
          <h5 className="mb-0">Gig Cart Verification Prompt Examples</h5>
          {notYetCreated ? (
            <Badge bg="secondary">Not Set (Facade Default Active)</Badge>
          ) : (
            <Badge bg="primary">Custom</Badge>
          )}
          {hasChanges && (
            <Badge bg="warning" text="dark">
              Unsaved Changes
            </Badge>
          )}
        </div>
        <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || !hasChanges}>
          {saving ? <Spinner animation="border" size="sm" /> : <Save size={14} className="me-1" />}
          Save
        </Button>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
          <AlertTriangle size={14} className="me-1" />
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)} className="mb-3">
          <CheckCircle size={14} className="me-1" />
          {success}
        </Alert>
      )}

      <Row style={{ height: 'calc(100% - 80px)' }}>
        <Col md={8} className="h-100">
          <Card className="h-100 d-flex flex-column">
            <Card.Header className="d-flex align-items-center justify-content-between py-2">
              <span className="small fw-medium">
                Site Config: <code>{GIG_CART_VERIFICATION_PROMPT_CONFIG_KEY}</code>
              </span>
              {version > 0 && (
                <span className="d-flex align-items-center gap-1 small text-muted">
                  <Clock size={12} />
                  {new Date(version < 1e12 ? version * 1000 : version).toLocaleString()}
                </span>
              )}
            </Card.Header>
            <Card.Body className="p-0 flex-grow-1" style={{ minHeight: 0 }}>
              <textarea
                aria-label="Gig cart verification prompt JSON configuration"
                value={configText}
                onChange={(event) => setConfigText(event.target.value)}
                className="w-100 h-100 border-0 p-3"
                style={{
                  resize: 'none',
                  fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  fontSize: '0.85rem',
                  lineHeight: 1.6,
                  outline: 'none',
                  backgroundColor: '#1e1e1e',
                  color: '#d4d4d4',
                }}
                spellCheck={false}
              />
            </Card.Body>
          </Card>
        </Col>

        <Col md={4} className="h-100" style={{ overflowY: 'auto' }}>
          <Card>
            <Card.Header className="py-2">
              <span className="small fw-medium">Guide</span>
            </Card.Header>
            <Card.Body className="small">
              <p className="mb-2">
                These synthetic few-shot examples teach the LLM how to transcribe a Coupang cart and match it to the
                trusted order lines.
              </p>
              <h6 className="mt-3 mb-2" style={{ fontSize: '0.8rem' }}>
                Rules
              </h6>
              <ul className="mb-2" style={{ fontSize: '0.8rem', paddingLeft: '1.2rem' }}>
                <li>Keep between 1 and 3 examples. Each example must stay within 10,000 characters.</li>
                <li>
                  <code>lineTotalKrw</code> is the total payable amount for the whole cart row, not the unit price.
                </li>
                <li>Use an already displayed row subtotal as-is; do not multiply it by cart quantity again.</li>
                <li>Every assigned or missing item ID must exist in that example&apos;s input order lines.</li>
              </ul>
              <h6 className="mt-3 mb-2" style={{ fontSize: '0.8rem' }}>
                Safety
              </h6>
              <ul className="mb-0" style={{ fontSize: '0.8rem', paddingLeft: '1.2rem' }}>
                <li>Use synthetic cart HTML only. Never paste customer, order, session, cookie, or address data.</li>
                <li>If any example is invalid, the Facade ignores the entire config and uses its built-in default.</li>
                <li>Changes are loaded for new cart verifications without a code deployment.</li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
